!(function () {
  "use strict";

  const selectors = {
    collectionSidebar: "[data-collection-sidebar]",
    rangeLeft: "[data-range-left]",
    rangeRight: "[data-range-right]",
    rangeLine: "[data-range-line]",
    rangeHolder: "[data-range-holder]",
    fieldPriceMin: "[data-field-price-min]",
    fieldPriceMax: "[data-field-price-max]",
    currencySymbol: ".filter__price__currency",
  };

  const attributes = {
    min: "data-se-min",
    max: "data-se-max",
    minValue: "data-se-min-value",
    maxValue: "data-se-max-value",
    step: "data-se-step",
    filterUpdate: "data-range-filter-update",
  };

  const classes = {
    initialized: "is-initialized",
  };

  if (customElements.get("range-slider")) return;

  customElements.define(
    "range-slider",
    class extends HTMLElement {
      constructor() {
        super();

        this.sidebar = this.closest(selectors.collectionSidebar);
        this.holder = this.closest(selectors.rangeHolder);

        this.sidebarTransitionEvent = (e) => this.onSidebarTransitionEnd(e);
        this.resizeEvent = () => {
          this.connectedCallback();
          this.sidebar.addEventListener("transitionend", this.sidebarTransitionEvent);
        };
        this.onMoveEvent = (e) => this.onMove(e);
        this.onStopEvent = () => this.onStop();
        this.onStartEvent = (e) => this.onStart(e);
        this.onKeydownEvent = (e) => this.onKeydown(e);
        // Debounced so holding/repeating an arrow key doesn't trigger the
        // AJAX filter update (and the sidebar rebuild that comes with it,
        // which would otherwise knock focus off the handle) on every single
        // keypress - only once the user pauses, same as the price text
        // inputs already do.
        this.debouncedOnChanged = window.theme.debounce((min, max) => this.onChanged(min, max), 500);

        this.startX = 0;
        this.x = 0;
        this.touchLeft = this.querySelector(selectors.rangeLeft);
        this.touchRight = this.querySelector(selectors.rangeRight);
        this.lineSpan = this.querySelector(selectors.rangeLine);
        this.min = parseFloat(this.getAttribute(attributes.min));
        this.max = parseFloat(this.getAttribute(attributes.max));
        this.step = 0;
        this.normalizeFact = 26;
        this.currencySymbol = (this.holder && this.holder.querySelector(selectors.currencySymbol)?.textContent.trim()) || "$";

        document.addEventListener("theme:resize:width", this.resizeEvent);
      }

      connectedCallback() {
        let minValue = this.min;
        if (this.hasAttribute(attributes.minValue)) minValue = parseFloat(this.getAttribute(attributes.minValue));

        let maxValue = this.max;
        if (this.hasAttribute(attributes.maxValue)) maxValue = parseFloat(this.getAttribute(attributes.maxValue));

        if (minValue < this.min) minValue = this.min;
        if (maxValue > this.max) maxValue = this.max;
        if (minValue > maxValue) minValue = maxValue;

        if (this.getAttribute(attributes.step)) this.step = Math.abs(parseFloat(this.getAttribute(attributes.step)));

        this.reset();

        this.maxX = this.offsetWidth - this.touchRight.offsetWidth;
        this.selectedTouch = null;
        this.initialValue = this.lineSpan.offsetWidth - this.normalizeFact;

        this.setupHandle(this.touchLeft, "Minimum price");
        this.setupHandle(this.touchRight, "Maximum price");

        this.setMinValue(minValue);
        this.setMaxValue(maxValue);

        this.touchLeft.addEventListener("mousedown", this.onStartEvent);
        this.touchRight.addEventListener("mousedown", this.onStartEvent);
        this.touchLeft.addEventListener("touchstart", this.onStartEvent, { passive: true });
        this.touchRight.addEventListener("touchstart", this.onStartEvent, { passive: true });
        this.touchLeft.addEventListener("keydown", this.onKeydownEvent);
        this.touchRight.addEventListener("keydown", this.onKeydownEvent);

        this.classList.add(classes.initialized);
      }

      /**
       * Make a drag handle keyboard-operable: focusable, with the ARIA
       * slider role/attributes screen readers need to announce its label
       * and current value.
       *
       * @param {Element} handle
       * @param {String} label
       */
      setupHandle(handle, label) {
        if (handle.hasAttribute("tabindex")) return;

        handle.setAttribute("tabindex", "0");
        handle.setAttribute("role", "slider");
        handle.setAttribute("aria-orientation", "horizontal");
        handle.setAttribute("aria-label", label);
        handle.setAttribute("aria-valuemin", this.min);
        handle.setAttribute("aria-valuemax", this.max);
      }

      /**
       * Keep a handle's ARIA value attributes in sync with its actual
       * value, whichever input method (mouse, touch or keyboard) changed
       * it.
       *
       * @param {Element} handle
       * @param {Number} value
       */
      updateAria(handle, value) {
        handle.setAttribute("aria-valuenow", value);
        handle.setAttribute("aria-valuetext", `${this.currencySymbol}${value}`);
      }

      reset() {
        this.touchLeft.style.left = "0px";
        this.touchRight.style.left = this.offsetWidth - this.touchLeft.offsetWidth + "px";
        this.lineSpan.style.marginLeft = "0px";
        this.lineSpan.style.width = this.offsetWidth - this.touchLeft.offsetWidth + "px";
        this.startX = 0;
        this.x = 0;
      }

      setMinValue(value) {
        const ratio = (value - this.min) / (this.max - this.min);

        this.touchLeft.style.left = Math.ceil(ratio * (this.offsetWidth - (this.touchLeft.offsetWidth + this.normalizeFact))) + "px";
        this.lineSpan.style.marginLeft = this.touchLeft.offsetLeft + "px";
        this.lineSpan.style.width = this.touchRight.offsetLeft - this.touchLeft.offsetLeft + "px";
        this.setAttribute(attributes.minValue, value);
        this.updateAria(this.touchLeft, value);
      }

      setMaxValue(value) {
        const ratio = (value - this.min) / (this.max - this.min);

        this.touchRight.style.left = Math.ceil(ratio * (this.offsetWidth - (this.touchLeft.offsetWidth + this.normalizeFact)) + this.normalizeFact) + "px";
        this.lineSpan.style.marginLeft = this.touchLeft.offsetLeft + "px";
        this.lineSpan.style.width = this.touchRight.offsetLeft - this.touchLeft.offsetLeft + "px";
        this.setAttribute(attributes.maxValue, value);
        this.updateAria(this.touchRight, value);
      }

      onStart(e) {
        let point = e;
        if (e.touches) point = e.touches[0];

        if (e.currentTarget === this.touchLeft) this.x = this.touchLeft.offsetLeft;
        else if (e.currentTarget === this.touchRight) this.x = this.touchRight.offsetLeft;

        this.startX = point.pageX - this.x;
        this.selectedTouch = e.currentTarget;

        document.addEventListener("mousemove", this.onMoveEvent);
        document.addEventListener("mouseup", this.onStopEvent);
        document.addEventListener("touchmove", this.onMoveEvent, { passive: true });
        document.addEventListener("touchend", this.onStopEvent, { passive: true });
      }

      onMove(e) {
        let point = e;
        if (e.touches) point = e.touches[0];

        this.x = point.pageX - this.startX;

        if (this.selectedTouch === this.touchLeft) {
          if (this.x > this.touchRight.offsetLeft - this.selectedTouch.offsetWidth + 10) {
            this.x = this.touchRight.offsetLeft - this.selectedTouch.offsetWidth + 10;
          } else if (this.x < 0) {
            this.x = 0;
          }
          this.selectedTouch.style.left = this.x + "px";
        } else if (this.selectedTouch === this.touchRight) {
          if (this.x < this.touchLeft.offsetLeft + this.touchLeft.offsetWidth - 10) {
            this.x = this.touchLeft.offsetLeft + this.touchLeft.offsetWidth - 10;
          } else if (this.x > this.maxX) {
            this.x = this.maxX;
          }
          this.selectedTouch.style.left = this.x + "px";
        }

        this.lineSpan.style.marginLeft = this.touchLeft.offsetLeft + "px";
        this.lineSpan.style.width = this.touchRight.offsetLeft - this.touchLeft.offsetLeft + "px";

        this.calculateValue();

        if (this.getAttribute("on-change")) {
          new Function("min, max", this.getAttribute("on-change"))(this.getAttribute(attributes.minValue), this.getAttribute(attributes.maxValue));
        }

        this.onChange(this.getAttribute(attributes.minValue), this.getAttribute(attributes.maxValue));
      }

      onStop() {
        document.removeEventListener("mousemove", this.onMoveEvent);
        document.removeEventListener("mouseup", this.onStopEvent);
        document.removeEventListener("touchmove", this.onMoveEvent);
        document.removeEventListener("touchend", this.onStopEvent);

        this.selectedTouch = null;
        this.calculateValue();
        this.onChanged(this.getAttribute(attributes.minValue), this.getAttribute(attributes.maxValue));
      }

      /**
       * Arrow/Home/End/Page keyboard support for a handle. Mirrors what
       * dragging does: move the handle, recompute the line, sync the
       * number inputs, then trigger the same "changed" event the AJAX
       * filter form listens for.
       *
       * @param {KeyboardEvent} e
       */
      onKeydown(e) {
        const isLeft = e.currentTarget === this.touchLeft;
        const isRight = e.currentTarget === this.touchRight;

        if (!isLeft && !isRight) return;

        const step = this.step || 1;
        const bigStep = step * 10;
        const currentMin = parseFloat(this.getAttribute(attributes.minValue));
        const currentMax = parseFloat(this.getAttribute(attributes.maxValue));
        let value = isLeft ? currentMin : currentMax;
        let handled = true;

        switch (e.key) {
          case "ArrowRight":
          case "ArrowUp":
            value += step;
            break;
          case "ArrowLeft":
          case "ArrowDown":
            value -= step;
            break;
          case "PageUp":
            value += bigStep;
            break;
          case "PageDown":
            value -= bigStep;
            break;
          case "Home":
            value = isLeft ? this.min : currentMin;
            break;
          case "End":
            value = isLeft ? currentMax : this.max;
            break;
          default:
            handled = false;
        }

        if (!handled) return;

        e.preventDefault();

        if (isLeft) {
          value = Math.min(Math.max(value, this.min), currentMax);
          this.setMinValue(value);
        } else {
          value = Math.max(Math.min(value, this.max), currentMin);
          this.setMaxValue(value);
        }

        this.onChange(this.getAttribute(attributes.minValue), this.getAttribute(attributes.maxValue));
        this.debouncedOnChanged(this.getAttribute(attributes.minValue), this.getAttribute(attributes.maxValue));
      }

      onChange(min, max) {
        const holder = this.closest(selectors.rangeHolder);
        if (!holder) return;

        const minField = holder.querySelector(selectors.fieldPriceMin);
        const maxField = holder.querySelector(selectors.fieldPriceMax);

        if (minField && maxField) {
          minField.value = min;
          maxField.value = max;
        }
      }

      onChanged() {
        if (this.hasAttribute(attributes.filterUpdate)) {
          this.dispatchEvent(new CustomEvent("theme:range:update", { bubbles: true }));
        }
      }

      calculateValue() {
        const ratio = (this.lineSpan.offsetWidth - this.normalizeFact) / this.initialValue;
        let minRatio = this.lineSpan.offsetLeft / this.initialValue;
        let maxRatio = minRatio + ratio;

        minRatio = minRatio * (this.max - this.min) + this.min;
        maxRatio = maxRatio * (this.max - this.min) + this.min;

        if (this.step !== 0) {
          minRatio = this.step * Math.floor(minRatio / this.step);
          maxRatio = this.step * Math.floor(maxRatio / this.step);
        }

        if (this.selectedTouch === this.touchLeft) {
          this.setAttribute(attributes.minValue, minRatio);
          this.updateAria(this.touchLeft, minRatio);
        }

        if (this.selectedTouch === this.touchRight) {
          this.setAttribute(attributes.maxValue, maxRatio);
          this.updateAria(this.touchRight, maxRatio);
        }
      }

      onSidebarTransitionEnd(e) {
        if (e.target === this.sidebar && e.propertyName === "min-width") {
          this.sidebar.removeEventListener("transitionend", this.sidebarTransitionEvent);
          this.connectedCallback();
        }
      }

      disconnectedCallback() {
        this.sidebar.removeEventListener("transitionend", this.sidebarTransitionEvent);
        if (this.resizeEvent) document.removeEventListener("theme:resize:width", this.resizeEvent);
      }
    }
  );
})();
